<template>
  <div>
    <v-row no-gutters style="gap: 0.5rem" class="align-center; mb-4">
      <v-col cols="auto" class="d-flex align-center">
        <span>{{ $i18n.t(`table.rzi${currentMetadata.type}.photos.title`) }}</span>
      </v-col>
      <v-col cols="auto">
        <base-rounded-button
          icon="mdi-plus"
          size="x-small"
          color="success"
          @click="openAddPhotoDialog(null)"
          :tooltip="$i18n.t(`table.rzi${currentMetadata.type}.photos.add`)"
        />
      </v-col>
    </v-row>
    <v-row no-gutters class="text-left">
      <v-col v-if="!computedPhotos.length" cols="12">
        <span class="text-caption">{{ $i18n.t(`table.rzi${currentMetadata.type}.photos.empty`) }}</span>
      </v-col>
      <v-col v-else cols="12">
        <v-row v-for="(photo, index) in computedPhotos" :key="index" no-gutters class="align-center" style="gap: 1rem;">
          <v-col>
            <v-row no-gutters class="align-center" style="gap: 1rem;">
              <v-col cols="2">
                <img :src="getPhotoUrl(photo)" @click="openLightBox(getPhotoUrl(photo))" class="image-preview" />
              </v-col>
              <v-col>
                <v-row no-gutters class="flex-column">
                  <v-col>
                    {{ photo.properties[currentMetadata.photos.attributes.name] }}
                  </v-col>
                  <v-col
                    class="text--disabled text-body-2"
                    v-if="photo.properties[currentMetadata.photos.attributes.description]"
                  >
                    {{ photo.properties[currentMetadata.photos.attributes.description] }}
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="auto">
                <v-row no-gutters style="gap: 0.5rem">
                  <v-col>
                    <v-icon @click="openAddPhotoDialog(photo)">
                      mdi-pencil
                    </v-icon>
                  </v-col>
                  <v-col>
                    <v-icon @click="openDeleteDialog(photo.id)">
                      mdi-delete
                    </v-icon>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row v-if="index !== computedPhotos.length - 1" class="my-2">
              <v-col>
                <v-divider :key="`divider-${index}`"></v-divider>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <image-lightbox :items="currentImage" :visible.sync="imagePreviewVisible" enableZoom />
    <main-input-dialog
      v-if="isDialogLoaded"
      :is-visible.sync="isDialogVisible"
      :title-translation="$i18n.t(`table.rziMonitoring.photos.${editItemId ? 'edit' : 'add'}`)"
      :agree-text="editItemId ? 'dialog.agreeEdit' : 'dialog.agreeAdd'"
      :loading="isDialogLoading"
      @agree="editItemId ? handleAddPhoto() : handleAddAttachment()"
      :inputs="inputs"
      dialog-width="45%"
      disable-default-form-bottom-padding
      ref="adding"
    >
      <template v-for="(input, index) in inputs" #[`${input.name}.input`]>
        <v-row v-if="input.name === currentMetadata.photos.attributes.photoFile" :key="index" class="mb-2">
          <v-col cols="1" style="min-width: 40px;"></v-col>
          <v-col>
            <file-input
              ref="file"
              :options="dropzoneOptions"
              show-added-files
              :events="{
                'vdropzone-success': onUploadSuccess,
                'vdropzone-error': onError,
                'vdropzone-file-added': onFileAdded,
              }"
            ></file-input>
          </v-col>
        </v-row>
        <v-row v-else-if="input.input" :key="index">
          <v-col>
            <feature-card-data-row
              v-bind="{
                ...input.input,
                ...(input.input.dataType?.name === 'text' ? { wrapText: true } : {}),
              }"
              v-model="values[input.name]"
              is-editing-on
              disable-width-limit
            >
              <template #[`row-input-${currentMetadata.photos.attributes.altitude}-append`]>
                <elevation-trigger :geometry="currentItemGeometry()" @changeValue="changeValueTrigger" />
              </template>
            </feature-card-data-row>
          </v-col>
        </v-row>
      </template>
    </main-input-dialog>
    <main-dialog
      :v-if="isDeleteDialogLoaded"
      :is-visible.sync="isDeleteDialogVisible"
      :loading="isDeleteDialogLoading"
      :title="$i18n.t('table.rziMonitoring.photos.deleteTitle')"
      :body="$i18n.t('table.rziMonitoring.photos.deleteBody')"
      @agree="handleDelete"
      agree-text="dialog.agreeDelete"
    />
  </div>
</template>

<script>
import ExifReader from 'exifreader';
import { get, call } from 'vuex-pathify';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

import FileInput from '@/components/FileInput';
import MainInputDialog from '@/components/MainInputDialog';
import MainDialog from '@/components/MainDialog';
import FeatureCardDataRow from '@/components/FeatureCardDataRow';
import ImageLightbox from '@/components/ImageLightbox';
import ElevationTrigger from '@/components/ElevationTrigger';

export default {
  name: 'RziPhotos',
  mixins: [prepareAttributesFeatureCardMixin],
  components: {
    FileInput,
    MainInputDialog,
    MainDialog,
    ImageLightbox,
    ElevationTrigger,
    FeatureCardDataRow,
  },
  props: {
    photos: {
      type: Array,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
    currentMetadata: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    isDeleteDialogLoaded: false,
    isDeleteDialogVisible: false,
    isDeleteDialogLoading: false,
    imagePreviewVisible: false,
    currentImage: [],
    isDialogLoaded: false,
    isDialogVisible: false,
    isDialogLoading: false,
    editItemId: false,
    values: {},
  }),
  computed: {
    token: get('authentication/token'),
    layersMetadata: get('layers/metadata'),
    computedPhotos: {
      get() {
        return this.photos;
      },
      set(value) {
        this.$emit('update:photos', value);
      },
    },
    inputs() {
      const formSchema = this.getForm(
        null,
        this.layersMetadata[this.currentMetadata.photos.datasource].attributes_schema.attributes
      );
      const formAttributes = this.editItemId
        ? this.currentMetadata?.photos.editFormAttributes
        : this.currentMetadata?.photos.formAttributes;
      return formAttributes.map(attribute => {
        const inputData = formSchema.find(el => el.attribute === attribute);
        const inputLabel = inputData?.label;
        return {
          name: attribute,
          straightTitle: inputLabel,
          align: 'start',
          isCustom: false,
          input: inputData,
        };
      });
    },
    dropzoneOptions() {
      return {
        url: `${process.env.VUE_APP_API_URL}/attachments`,
        method: 'post',
        autoProcessQueue: false,
        uploadMultiple: false,
        addRemoveLinks: true,
        paramName: 'file',
        maxFiles: 1,
        init: function() {
          this.on('maxfilesexceeded', function(file) {
            this.removeAllFiles();
            this.addFile(file);
          });
        },
      };
    },
  },
  methods: {
    deleteDatasourceFeature: call('layers/deleteDatasourceFeature'),
    editNonTopoFeatureAction: call('layers/editDatasourceFeature'),
    addNonTopoFeatureAction: call('layers/addDatasourceFeature'),
    addAttachment: call('relations/addAttachment'),
    openLightBox(url) {
      this.currentImage = [url];
      this.imagePreviewVisible = true;
    },
    getFileDropzone() {
      return [this.$refs.file].flat()?.[0]?.$refs.dropzone ?? null;
    },
    currentItemGeometry() {
      return (
        this.values[this.layersMetadata[this.currentMetadata.photos.datasource]?.attributes_schema.geometry_name] ??
        this.getGeometry({
          Longitude: this.values[this.currentMetadata.photos.attributes.lon],
          Latitude: this.values[this.currentMetadata.photos.attributes.lat],
        }) ??
        null
      );
    },
    getGeometry(gpsSection) {
      if (!gpsSection.Longitude || !gpsSection.Latitude) return null;
      return {
        coordinates: [gpsSection.Longitude, gpsSection.Latitude],
        crs: {
          type: 'name',
          properties: {
            name: 'EPSG:4326',
          },
        },
        type: 'Point',
      };
    },
    getPhotoUrl(photo) {
      const urlAttributes = photo.properties[this.currentMetadata.photos.attributes.photoFile];
      return `${process.env.VUE_APP_API_URL}${urlAttributes}?token=${this.token}`;
    },
    onError() {
      this.getFileDropzone()?.removeAllFiles();
      this.$emit('fileError');
    },
    onUploadSuccess(file, response) {
      this.getFileDropzone()?.removeAllFiles();
      this.values = {};
      this.$emit('fileUploaded', response?.data);
    },
    openDeleteDialog(id) {
      this.editItemId = id;
      this.isDeleteDialogLoaded = this.isDeleteDialogVisible = true;
    },
    openAddPhotoDialog(item) {
      this.getFileDropzone()?.removeAllFiles();
      this.editItemId = item?.id ?? null;
      this.values = {
        ...(item
          ? this.inputs.reduce((acc, input) => {
              acc[input.name] = item.properties[input.name];
              return acc;
            }, {})
          : {}),
        [this.currentMetadata.photos.attributes.mainId]: this.item.id,
      };
      this.isDialogLoaded = this.isDialogVisible = true;
    },
    async handleDelete() {
      this.isDeleteDialogLoading = true;
      try {
        await this.deleteDatasourceFeature({
          datasource: this.currentMetadata.photos.datasource,
          id: this.editItemId,
          params: {
            cascade: true,
          },
        });
        this.$emit('retrievePhotos', () => {
          this.editItemId = null;
          this.isDeleteDialogLoading = this.isDeleteDialogVisible = false;
        });
      } catch {
        this.isDeleteDialogLoading = false;
      }
    },
    async handleAddAttachment() {
      this.isDialogLoading = true;
      const files = this.getFileDropzone()?.getAcceptedFiles();
      if (files?.length < 1) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.filesRequired'),
        });
        return;
      }
      const formData = new FormData();
      formData.append('file', files[0]);
      formData.append('name', files[0].name);
      try {
        const r = await this.addAttachment(formData);
        this.values[this.currentMetadata.photos.attributes.photoFile] = `/attachments/${r.data.data[0]}/file`;
        await this.handleAddPhoto();
      } catch {
        this.isDialogLoading = false;
      }
    },
    changeValueTrigger(value) {
      this.values[this.currentMetadata.photos.attributes.altitude] = value;
      this.$nextTick(() => this.$forceUpdate());
    },
    async handleAddPhoto() {
      this.isDialogLoading = true;
      try {
        const staticData = {
          datasource: this.currentMetadata.photos.datasource,
          body: this.values,
        };
        if (this.editItemId) {
          await this.editNonTopoFeatureAction({
            ...staticData,
            id: this.editItemId,
          });
        } else {
          await this.addNonTopoFeatureAction(staticData);
        }
        this.$emit('retrievePhotos', () => {
          this.isDialogVisible = this.isDialogLoading = false;
        });
      } catch {
        this.isDialogLoading = false;
      }
    },
    async onFileAdded(file) {
      const photoBuffer = await file.arrayBuffer();
      const tags = ExifReader.load(photoBuffer, { expanded: true });
      const gpsSection = tags.gps;
      if (!gpsSection) {
        this.getFileDropzone()?.removeAllFiles();
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('table.rziMonitoring.photos.noGps'),
        });
        return;
      }
      const dtGeomName = this.layersMetadata[this.currentMetadata.photos.datasource]?.attributes_schema.geometry_name;
      this.values[dtGeomName] = this.getGeometry(gpsSection);
      if (gpsSection.Altitude && !this.values[this.currentMetadata.photos.attributes.altitude])
        this.values[this.currentMetadata.photos.attributes.altitude] = gpsSection.Altitude;
      this.$nextTick(() => this.$forceUpdate());
    },
  },
  mounted() {},
};
</script>

<style scoped lang="scss">
.image-preview {
  width: 100%;
  height: 100%;
  min-height: 100px;
  max-height: 120px;
  object-fit: contain;
}
.image-preview:hover {
  cursor: pointer;
}
</style>
